import type React from 'react';
import { useRef, useState } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { CalendarMonthOutlineIcon } from '@carboncopyco/icons';
import { format, isValid, parse } from 'date-fns';
import { Button } from '../Button';
import { type Placement, Tooltip, type TooltipProps } from '../Tooltip';

export type InputDateProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'type'
> & {
  hasTodayButton?: boolean;
  dropdownPlacement?: Placement;
  dropdownOffset?: TooltipProps['offset'];
  onChange: (date: string) => void;
};

const DATE_FORMAT = 'MM/dd/yyyy';

export const InputDate = ({
  value,
  hasTodayButton,
  onChange,
  dropdownPlacement = 'bottom-start',
  dropdownOffset = [-12, 4],
  ...rest
}: InputDateProps) => {
  const anchorRef = useRef<HTMLInputElement>(null);
  const [month, setMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [inputValue, setInputValue] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const defaultClassNames = getDefaultClassNames();

  const toggleDialog = () => setIsDialogOpen(!isDialogOpen);

  const handleDayPickerSelect = (date: Date) => {
    if (!date) {
      setInputValue('');
      setSelectedDate(undefined);
    } else {
      setSelectedDate(date);
      setInputValue(format(date, DATE_FORMAT));
    }
    setIsDialogOpen(false);
    onChange(format(date, DATE_FORMAT));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value); // keep the input value in sync
    const parsedDate = parse(e.target.value, DATE_FORMAT, new Date());

    if (isValid(parsedDate)) {
      setSelectedDate(parsedDate);
      setMonth(parsedDate);
      onChange(e.target.value);
    } else {
      setSelectedDate(undefined);
      onChange('');
    }
  };

  return (
    <div className="relative">
      <div
        className="flex items-center justify-between gap-1
                     rounded-md
                     relative
                     placeholder-ds-natural-300
                     leading-ds-300
                     box-border
                     outline
                     !outline-1
                     outline-offset-[-1px]
                     tracking-ds-normal
                     font-ds-inter
                     bg-white text-ds-natural-600 outline-ds-natural-100 hover:outline-ds-natural-300 focus:outline-ds-primary-300 focus-within:outline-ds-primary-300 hover:focus:outline-ds-primary-300 hover:focus-within:outline-ds-primary-300 px-3 py-2 text-ds-400 font-ds-normal h-ds-lg-input"
      >
        <input
          value={inputValue}
          type="text"
          className="h-9 outline-none border-none p-0 focus:ring-0 flex-1 bg-white w-full"
          placeholder={DATE_FORMAT}
          onChange={handleInputChange}
          ref={anchorRef}
          {...rest}
        />

        <div className="absolute right-2.5 top-px bottom-px flex items-center bg-white">
          <button
            style={{ fontSize: 'inherit' }}
            onClick={toggleDialog}
            aria-controls="dialog"
            aria-haspopup="dialog"
            aria-expanded={isDialogOpen}
            aria-label="Open calendar to choose the date"
          >
            <CalendarMonthOutlineIcon />
          </button>
        </div>
      </div>
      {isDialogOpen && (
        <Tooltip
          visible={isDialogOpen}
          interactive
          noArrow
          zIndex={9999}
          placement={dropdownPlacement}
          offset={dropdownOffset}
          appendTo={() => document.body}
          anchorRef={anchorRef}
          background="light"
          fixedWidth={false}
          onClickOutside={() => {
            setIsDialogOpen(false);
          }}
        >
          <div className="flex flex-col rounded-md pointer-events-auto bg-ds-accent3-100 flex p-4 pt-2 text-ds-natural-600 font-ds-inter text-ds-300 font-ds-medium tracking-ds-tight leading-ds-200">
            <DayPicker
              month={month}
              onMonthChange={setMonth}
              autoFocus
              mode="single"
              selected={selectedDate}
              onSelect={handleDayPickerSelect}
              classNames={{
                today:
                  'rounded-full outline outline-solid outline-1 outline-ds-natural-200',
                selected: 'rounded-full bg-ds-primary-300 text-white',
                chevron: `${defaultClassNames.chevron} !fill-ds-primary-300`,
                day_button: `${defaultClassNames.day_button} focus-visible:outline focus-visible:outline-2 focus-visible:outline-ds-primary-400`,
              }}
            />

            {hasTodayButton && (
              <div className="flex justify-end pt-2">
                <Button
                  small
                  secondary
                  link
                  label="Today"
                  onClick={() => {
                    setIsDialogOpen(false);
                    const today = new Date();
                    setSelectedDate(today);
                    setInputValue(format(today, DATE_FORMAT));
                    onChange(format(today, DATE_FORMAT));
                  }}
                />
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

InputDate.displayName = 'InputDate';
